import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, RefreshCw, Phone, FileText, AlertTriangle,
  TrendingUp, ShieldCheck, Users, Activity, Eye, Mail,
  CheckCircle2,
} from "lucide-react";
import usePageview from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.16n — Dabble morning brief preview.
//
// Built for Jon Robin (Dabble co-founder, consumer-app builder, NOT an
// industrial control-room operator). The existing /industrial-demo
// shows raw streaming telemetry — exactly the wrong register for a
// social-betting founder. This page shows what the actual MORNING
// BRIEF would look like in his inbox at 7am every day.
//
// Four cards, plain English, specific named customers, specific
// regulator status, specific tipsters, specific suggested actions.
// No Bloomberg-terminal streaming widgets. No tag tables. No charts.
// Just: what's the situation, who do I call, what do I do today.
//
// "Refresh" cycles through three sample scenarios so a screen-share
// demo can show different days without feeling repetitive.

const SCENARIOS = [
  // Friday morning before a big NRL fixture
  {
    day: "Friday",
    date_pretty: "23 May 2026",
    book: {
      pnl_week: "+A$847,200",
      pnl_band: "+9.2% vs plan",
      tone: "ok",
      headline: "You're up A$847k on the week. One market is wobbling.",
      detail: "73% of money on the Sharks vs Storm Sunday-night NRL. Liability on the Sharks side is creeping toward your A$420k ceiling. Your trading desk should look at this before lunch, not at kickoff.",
      action: "Get trading to widen the Storm price OR lay off A$120k on Betfair.",
    },
    customers: [
      {
        handle: "Jamie K.",
        tag: "NCPF · harm intervention",
        reason: "Chased losses Tuesday night through Thursday morning. Deposited A$1,420 in 36 hours. Currently down A$1,180. NCPF threshold crossed this morning.",
        action: "Manual outreach call before 11am. Offer a 7-day deposit limit reduction.",
        tone: "high",
      },
      {
        handle: "FastBets44",
        tag: "AUSTRAC · AML review",
        reason: "New customer · 14 days old. Deposited A$8,500 across 3 different cards in 48 hours. Placed minimal bets. Withdrew A$6,200 yesterday. Classic 'wash through the book' signature.",
        action: "Freeze the account pending source-of-funds verification. SMR filing window: 24 hours.",
        tone: "high",
      },
      {
        handle: "PrettyTipster_P",
        tag: "Tipster · concentration risk",
        reason: "Their 800+ followers placed A$340k on Friday's matches collectively. If their streak ends Sunday, churn could spike 20% next week.",
        action: "Send retention package today. Pre-empt their LinkedIn DMs from Sportsbet.",
        tone: "watch",
      },
    ],
    regulator: [
      { agency: "AUSTRAC", status: "ON TRACK", detail: "6 SMRs in the queue. 3 within the 24hr clock. MLRO has the list. Filed daily total: 11/11 within window this month." },
      { agency: "ACMA", status: "ON TRACK", detail: "Bet acceptance latency averaged 142ms this week. Well under your 200ms SLA. No service-standard exposure." },
      { agency: "NCPF", status: "AT THRESHOLD", detail: "4 active intervention cases. 1 needs manual escalation today (Jamie K. — see above). Cohort report ready for VIC regulator request expected next week." },
      { agency: "BetStop", status: "ON TRACK", detail: "Register check passing 100%. Latency p95 = 89ms. Fail-closed alert disabled all week — meaning no failure scenarios reached." },
    ],
    tipsters: {
      headline: "5 tipsters drove A$2.4M of GMV this week. That's 61% of social-driven volume.",
      detail: [
        "PrettyTipster_P · 22% MoM growth · concentration risk rising (see Customers card)",
        "BroncosKing · steady · A$640k cohort GMV · low risk",
        "TwoUpQueen · +11% WoW follower growth · trending up · keep eye on",
        "Jordan_S · DOWN 31% WoW · possible departure signal · reach out within 48h",
        "NewKid_22 · brand new · 18% WoW follower growth · monitor for fast cascade build",
      ],
    },
  },
  // Monday morning after a big weekend
  {
    day: "Monday",
    date_pretty: "26 May 2026",
    book: {
      pnl_week: "+A$1,142,000",
      pnl_band: "+14.6% vs plan",
      tone: "ok",
      headline: "Best week of the financial year. NRL + AFL weekend delivered.",
      detail: "Storm covered, Sharks blowout went your way. Tipster cascades stayed inside the band. One outlier: cross-code multi cluster on Saturday paid out A$84k more than modelled — likely social-graph copy effect through PrettyTipster_P's follower set.",
      action: "Trading desk to review the multi-modelling assumptions for follower-clustered bets. Bonus chat at the All Hands.",
    },
    customers: [
      {
        handle: "PrettyTipster_P",
        tag: "Tipster · post-win concentration",
        reason: "Their followers all won on Saturday. Followers now up 18% on the week. Risk: they go all-in next weekend and concentration multiplies. Also risk: Sportsbet now offers them double.",
        action: "Retention call this morning. Loyalty bonus + exclusive market access offer.",
        tone: "watch",
      },
      {
        handle: "Jamie K.",
        tag: "NCPF · post-intervention check",
        reason: "Took the 7-day deposit limit Friday. Spent A$0 this weekend. Self-reported on app that it 'felt good to step back'. NCPF intervention working as designed.",
        action: "Schedule 14-day check-in. Document outcome in NCPF log. No further action.",
        tone: "ok",
      },
      {
        handle: "GoldCoaster99",
        tag: "AUSTRAC · enhanced due diligence",
        reason: "Deposited A$24,500 over the weekend across 6 transactions. Wagered most of it. Net loss A$3,200. Pattern: large but not structured. Source of funds known. Likely legitimate VIP, but threshold crossed.",
        action: "ECDD process. Source-of-funds re-verification. Compliance to call by Wednesday.",
        tone: "watch",
      },
    ],
    regulator: [
      { agency: "AUSTRAC", status: "ON TRACK", detail: "Weekend produced 4 new SMRs. All under 24hr clock. MLRO has the brief at 9am." },
      { agency: "ACMA", status: "WATCH", detail: "Saturday 7:42pm — bet acceptance latency spiked to 312ms for 90 seconds. Outside SLA. App perf team has root cause: payment-processor timeout cascading. Fixed by 7:51pm. Service-standard report: incident log ready, no customer comp triggered." },
      { agency: "NCPF", status: "ON TRACK", detail: "1 case closed (Jamie K.). 3 active. 2 new this morning from weekend behaviour." },
      { agency: "BetStop", status: "ON TRACK", detail: "Register check passing 100% all weekend including peak load." },
    ],
    tipsters: {
      headline: "Top 10 tipsters drove A$3.1M weekend GMV. PrettyTipster_P alone drove A$680k.",
      detail: [
        "PrettyTipster_P · weekend hero · followers up 18% · retention call this morning is critical",
        "BroncosKing · solid weekend · A$910k cohort GMV · stable",
        "TwoUpQueen · 4 new tipster mentions on Twitter · A$200k GMV vs A$150k last week",
        "Jordan_S · still flat · check-in still pending · do this today",
        "NewKid_22 · 28% WoW follower growth · cascade depth now 4 levels · risk + opportunity",
      ],
    },
  },
  // Wednesday quiet midweek
  {
    day: "Wednesday",
    date_pretty: "28 May 2026",
    book: {
      pnl_week: "+A$284,000",
      pnl_band: "+3.1% vs plan · mid-week pace",
      tone: "ok",
      headline: "Quiet midweek. A-League Tuesday turnover normal. Cup-day on Saturday is the next big surge.",
      detail: "Concentration risk on the Cup-day favourites already showing. Two tipsters (BroncosKing, NewKid_22) both posted big tips. Their combined follower set placed A$210k on Crown Voyage in the last 6 hours.",
      action: "Trading to monitor Crown Voyage market. Pre-build a layoff plan for if cascade gets to 3x current size.",
    },
    customers: [
      {
        handle: "ChasingSarah_77",
        tag: "NCPF · early-warning",
        reason: "30-day harm-onset model just flagged this customer. Deposit velocity quietly doubled over 14 days. Late-night sessions every Mon/Tue. Hasn't crossed crisis threshold YET — but trajectory says she will inside 21 days.",
        action: "Send the soft 'we noticed you've been on a bit more, all good?' email this afternoon. Track response. Pre-empt the crisis.",
        tone: "watch",
      },
      {
        handle: "Jordan_S",
        tag: "Tipster · churn signal",
        reason: "Hasn't posted a tip in 4 days. Followers down 31% WoW. App login down. He's leaving.",
        action: "Founder-to-tipster outreach today. If he's going to Sportsbet, find out why. If he's burnt out, offer a sabbatical-and-comeback structure.",
        tone: "high",
      },
      {
        handle: "GoldCoaster99",
        tag: "AUSTRAC · ECDD outcome",
        reason: "Source-of-funds confirmed yesterday. Legitimate Gold Coast property developer. ECDD passes. Account back to standard monitoring.",
        action: "Close the case. Document. No further action.",
        tone: "ok",
      },
    ],
    regulator: [
      { agency: "AUSTRAC", status: "ON TRACK", detail: "2 SMRs filed this week. 1 case closed (GoldCoaster99). Mid-month dashboard at 11/11 within window." },
      { agency: "ACMA", status: "ON TRACK", detail: "Latency back to 140ms baseline since Saturday incident. Service-standard incident log filed. No regulator follow-up required." },
      { agency: "NCPF", status: "ON TRACK", detail: "5 active interventions. 1 new pre-emptive case (ChasingSarah_77 above). 2 closed successfully this week." },
      { agency: "BetStop", status: "ON TRACK", detail: "Register check passing 100%. p95 latency 91ms." },
    ],
    tipsters: {
      headline: "Quiet midweek. Top 5 tipsters drove A$640k. Cup-day on Saturday will be 4x this.",
      detail: [
        "PrettyTipster_P · post-weekend cooldown · stable · monitor for next cascade Friday",
        "BroncosKing · posted Crown Voyage tip Tuesday · 380 copy-bets in 6 hours",
        "TwoUpQueen · in-form · A$220k WoW cohort GMV",
        "Jordan_S · departure signal hardening · do the call TODAY",
        "NewKid_22 · cascade depth now 5 levels · highest in the network · watch closely",
      ],
    },
  },
];

export default function DabbleLive() {
  usePageview("/dabble/live");
  useNoIndex();

  const [scenarioIndex, setScenarioIndex] = useState(0);
  const scenario = SCENARIOS[scenarioIndex];

  const refresh = () => {
    setScenarioIndex((i) => (i + 1) % SCENARIOS.length);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dabble-live-page">
      <BespokePageRibbon prospectName="Dabble · AU Social Bookmaker" />
      <BespokeWatermark pageLabel="DABBLE-LIVE" position="top" />

      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/dabble" className="flex items-center gap-2" data-testid="dabble-live-back">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · DABBLE · MORNING BRIEF PREVIEW
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/industrial-demo?profile=sportsbook"
              data-testid="dabble-live-engineering-view"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000] hidden sm:inline-flex items-center gap-1.5"
              title="The same data, in raw streaming-telemetry form — for the CTO"
            >
              <Activity className="w-3 h-3" /> Engineering view
            </Link>
            <Link
              to="/dabble#engage"
              data-testid="dabble-live-engage-cta"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Continue the conversation
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FFB000] mb-3">
            // YOUR MORNING BRIEF · WHAT THIS LOOKS LIKE EVERY DAY · 7AM IN YOUR INBOX
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            <span className="text-[#FFB000]">Dabble Today</span> &nbsp;·&nbsp; {scenario.day}, {scenario.date_pretty}
          </h1>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            This is what your team would walk into every morning if DARA was already running. Not a dashboard — a one-page brief. Plain English. Specific names. Specific actions. The work has been done overnight.
          </p>
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <Mail className="w-3 h-3" />
              <span>simulated sample · click refresh to see a different day</span>
            </div>
            <button
              type="button"
              onClick={refresh}
              data-testid="dabble-live-refresh"
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#FFB000] hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" /> Show a different day
            </button>
          </div>
        </div>
      </section>

      {/* 4 cards · 2x2 on desktop, stacked on mobile */}
      <section className="bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5" data-testid="dabble-live-cards">

            {/* CARD 1 — THE BOOK */}
            <Card
              label="01 · THE BOOK TODAY"
              Icon={TrendingUp}
              testid="card-book"
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-heading font-black text-3xl text-[#00E676]">{scenario.book.pnl_week}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{scenario.book.pnl_band}</span>
              </div>
              <p className="text-base font-bold text-white leading-snug mb-2">{scenario.book.headline}</p>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">{scenario.book.detail}</p>
              <ActionLine text={scenario.book.action} />
            </Card>

            {/* CARD 2 — CUSTOMERS TO CALL */}
            <Card
              label={`02 · CUSTOMERS TO CALL TODAY (${scenario.customers.length})`}
              Icon={Phone}
              testid="card-customers"
            >
              <div className="space-y-3.5">
                {scenario.customers.map((c, i) => (
                  <CustomerRow key={i} customer={c} />
                ))}
              </div>
            </Card>

            {/* CARD 3 — REGULATOR */}
            <Card
              label="03 · REGULATOR STANDING"
              Icon={ShieldCheck}
              testid="card-regulator"
            >
              <div className="space-y-2.5">
                {scenario.regulator.map((r, i) => (
                  <RegulatorRow key={i} row={r} />
                ))}
              </div>
            </Card>

            {/* CARD 4 — TIPSTER ECONOMY */}
            <Card
              label="04 · THE TIPSTER ECONOMY"
              Icon={Users}
              testid="card-tipsters"
            >
              <p className="text-base font-bold text-white leading-snug mb-3">{scenario.tipsters.headline}</p>
              <ul className="space-y-1.5">
                {scenario.tipsters.detail.map((d, i) => (
                  <li key={i} className="text-sm text-zinc-300 leading-relaxed pl-3 border-l border-zinc-800">{d}</li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Below the cards · context strip */}
          <div className="mt-10 border-t border-zinc-900 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatPoint
              Icon={FileText}
              label="WHAT THIS REPLACES"
              body="The 30 minutes your team spends every morning pulling numbers from 5 different tools, plus the 2 hours your compliance lead spends every Friday compiling weekly reports."
            />
            <StatPoint
              Icon={Eye}
              label="WHAT'S INCLUDED"
              body="Book P&amp;L · concentration risk · customer harm signals · AUSTRAC queue · ACMA latency · NCPF interventions · BetStop checks · tipster economy. One page. Every morning."
            />
            <StatPoint
              Icon={CheckCircle2}
              label="WHO READS IT"
              body="CEO scans card 1 in 30 seconds. Compliance lead lives in cards 2 and 3. Head of growth lives in card 4. Same brief, four different uses."
            />
          </div>

          {/* CTA strip */}
          <div className="mt-10 flex items-center justify-between flex-wrap gap-4 border border-[#FFB000]/30 bg-[#FFB000]/5 px-6 py-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">// NEXT STEP</div>
              <div className="text-base sm:text-lg font-bold tracking-tight">Pick one capability. 12 weeks. $0 fee for the pilot.</div>
              <div className="text-xs text-zinc-400 mt-1">DARA picks up the cost of the first pilot in exchange for a case study + 2 reference calls + first-look on expansion.</div>
            </div>
            <Link
              to="/dabble#engage"
              data-testid="dabble-live-bottom-cta"
              className="font-mono text-[10px] uppercase tracking-widest bg-[#FFB000] text-black px-4 py-2.5 hover:opacity-90 flex items-center gap-1.5"
            >
              Schedule the call <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom strip */}
      <div className="bg-black border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between flex-wrap gap-3">
          <span>// DARA OS · BESPOKE FOR DABBLE · {new Date().toLocaleString("en-AU", { month: "short", year: "numeric" }).toUpperCase()}</span>
          <Link to="/dabble" className="hover:text-[#FFB000] flex items-center gap-1.5" data-testid="dabble-live-back-to-proposal">
            <ArrowRight className="w-3 h-3 rotate-180" /> Back to Dabble × DARA proposal
          </Link>
        </div>
      </div>
      <BespokeWatermark pageLabel="DABBLE-LIVE" position="bottom" />
    </div>
  );
}

function Card({ label, Icon, testid, children }) {
  return (
    <div className="bg-black border border-zinc-900 p-5 sm:p-6" data-testid={`dabble-live-${testid}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#FFB000]" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// {label}</span>
      </div>
      {children}
    </div>
  );
}

function ActionLine({ text }) {
  return (
    <div className="mt-1 border-l-2 border-[#FFB000] bg-[#FFB000]/5 pl-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-0.5">▸ ACTION TODAY</div>
      <p className="text-sm text-zinc-100 leading-snug">{text}</p>
    </div>
  );
}

function CustomerRow({ customer }) {
  const toneClasses = {
    high: { dot: "bg-[#FF6B47]", tag: "text-[#FF6B47]" },
    watch: { dot: "bg-[#FFB000]", tag: "text-[#FFB000]" },
    ok: { dot: "bg-[#00E676]", tag: "text-[#00E676]" },
  }[customer.tone];
  return (
    <div className="border-l-2 border-zinc-800 pl-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${toneClasses.dot} ${customer.tone !== "ok" ? "animate-pulse" : ""}`} />
        <span className="text-sm font-bold text-white">{customer.handle}</span>
        <span className={`font-mono text-[9px] uppercase tracking-widest ${toneClasses.tag}`}>· {customer.tag}</span>
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed mb-1">{customer.reason}</p>
      <p className="text-xs text-zinc-100 leading-relaxed font-bold">→ {customer.action}</p>
    </div>
  );
}

function RegulatorRow({ row }) {
  const statusColour = {
    "ON TRACK": "text-[#00E676]",
    "WATCH": "text-[#FFB000]",
    "AT THRESHOLD": "text-[#FFB000]",
    "BREACH": "text-[#FF6B47]",
  }[row.status] || "text-zinc-400";
  return (
    <div className="border-l-2 border-zinc-800 pl-3 py-1">
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-mono text-xs font-bold tracking-tight text-white">{row.agency}</span>
        <span className={`font-mono text-[10px] uppercase tracking-widest ${statusColour}`}>· {row.status}</span>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">{row.detail}</p>
    </div>
  );
}

function StatPoint({ Icon, label, body }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[#FFB000]" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// {label}</span>
      </div>
      <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}
